import React, { useState, useEffect, useRef } from 'react';
import { MdEmail, MdLockReset } from 'react-icons/md';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import InputField from '../components/ui/InputField';
import Logo from '../assets/manak-logo.png';
import toast from 'react-hot-toast';
import { BiLoaderAlt } from 'react-icons/bi';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { forgotPassword, verifyOTP } from '../redux/actions/authActions';
import { clearAuthError, resetForgotPasswordState, resetAll } from '../redux/slices/authSlice';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loading, error, forgotPasswordSuccess, otpVerified, verificationToken, userExists } = useAppSelector(state => state.auth);
  
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [formData, setFormData] = useState({
    email: '',
    role: 'employee', // Default role
    otp: ''
  });
  
  // Create refs for OTP inputs
  const otpInputs = useRef<(HTMLInputElement | null)[]>([]);
  const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(''));

  // Role options
  const roleOptions = [
    { display: 'Admin', value: 'admin' },
    { display: 'User', value: 'employee' },
  ];

  // Clear any existing errors when component mounts and ensure we start on email step
  useEffect(() => {
    dispatch(clearAuthError());
    dispatch(resetForgotPasswordState());
    setStep('email');
  }, [dispatch]);

  // We don't need the success useEffect anymore since we handle it in the submit handler

  // Handle successful OTP verification
  useEffect(() => {
    if (otpVerified && verificationToken) {
      toast.success('OTP verified successfully!');
      // Navigate to reset password page with token
      navigate(`/auth/reset-password/${verificationToken}?role=${formData.role}`);
    }
  }, [otpVerified, verificationToken, navigate, formData.role]);

  // Show error message if there is one and handle error state cleanup
  useEffect(() => {
    if (error) {
      // Display error toast
      toast.error(error);
      
      // Reset step to email form if there's an error with forgot password
      if (error.includes("doesn't exist") || error.includes("Invalid email")) {
        setStep('email');
        // Ensure OTP values are cleared
        setOtpValues(Array(6).fill(''));
      }
      
      // Clear the error after displaying it to prevent duplicate toasts
      setTimeout(() => {
        dispatch(clearAuthError());
      }, 100);
    }
  }, [error, dispatch]);

  // Update formData.otp whenever otpValues changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      otp: otpValues.join('')
    }));
  }, [otpValues]);

  // Clean up effect when component unmounts
  useEffect(() => {
    return () => {
      // Clear Redux state
      dispatch(resetAll());
      dispatch(clearAuthError());
      
      // Reset component state
      setStep('email');
      setOtpValues(Array(6).fill(''));
    };
  }, [dispatch]);

  // Handle browser back button with improved state reset
  useEffect(() => {
    const handlePopState = () => {
      if (step === 'otp') {
        // If we're on OTP step and back button is pressed, reset everything
        setStep('email');
        setOtpValues(Array(6).fill(''));
        
        // Reset Redux state to clear any loading or error states
        dispatch(resetAll());
        dispatch(clearAuthError());
        
        // Reset form data but preserve the email for better UX
        setFormData(prev => ({
          ...prev,
          otp: '',
          // Keep email and role for user convenience
        }));
        
        // Prevent default back behavior and add a fresh history entry
        window.history.pushState({ preventGoBack: true }, document.title, window.location.href);
        return;
      }
    };

    // Add a history entry with state when moving to OTP step to enable back button
    if (step === 'otp') {
      window.history.pushState({ inOtpStep: true }, document.title, window.location.href);
    }

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [step, dispatch]);

  // Reset OTP values when step changes to email
  useEffect(() => {
    if (step === 'email') {
      setOtpValues(Array(6).fill(''));
    }
  }, [step]);

  // Handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value.slice(0, 1); // Only take the first character
    setOtpValues(newOtpValues);

    // Auto-focus next input if current input is filled
    if (value && index < 5) {
      otpInputs.current[index + 1]?.focus();
    }
  };

  // Handle keydown for backspace to move to previous input
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  // Handle paste event for OTP
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    if (!/^\d+$/.test(pastedData)) return; // Only allow digits
    
    const digits = pastedData.slice(0, 6).split('');
    const newOtpValues = [...otpValues];
    
    digits.forEach((digit, index) => {
      if (index < 6) {
        newOtpValues[index] = digit;
      }
    });
    
    setOtpValues(newOtpValues);
    
    // Focus the next empty input or the last input
    const nextEmptyIndex = newOtpValues.findIndex(val => !val);
    if (nextEmptyIndex !== -1 && nextEmptyIndex < 6) {
      otpInputs.current[nextEmptyIndex]?.focus();
    } else {
      otpInputs.current[5]?.focus();
    }
  };

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any existing errors first
    dispatch(clearAuthError());
    
    // Validate email
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    // Validate email format with basic regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      // Dispatch action and handle potential errors
      const result = await dispatch(forgotPassword({
        email: formData.email,
        role: formData.role
      })).unwrap();
      
      // If we get here, it means the action was successful (not rejected)
      if (result.userExists === false) {
        // Handle the case where the user doesn't exist but the API returned success: true
        toast.error("User with this email doesn't exist");
        setStep('email');
      } else {
        // Handle success case (only move to OTP if user exists)
        toast.success('If an account exists with this email, you will receive an OTP.');
        setStep('otp');
      }
    } catch (error: any) {
      // The action was rejected - error will be shown via the error useEffect
      console.error('Failed to send OTP:', error);
      // Ensure we stay on email step
      setStep('email');
    }
  };

  const handleSubmitOTP = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any existing errors first
    dispatch(clearAuthError());
    
    // Validate complete OTP
    if (otpValues.some(val => !val)) {
      toast.error('Please enter the complete 6-digit OTP sent to your email');
      return;
    }

    // Ensure we have email (should be there, but double-check)
    if (!formData.email) {
      toast.error('Email information is missing. Please go back and try again.');
      setStep('email');
      return;
    }

    dispatch(verifyOTP({
      email: formData.email,
      otp: otpValues.join(''),
      role: formData.role
    }));
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-white relative overflow-hidden">
      {/* Background Pattern SVG */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern
            id="wave-pattern"
            patternUnits="userSpaceOnUse"
            width="100"
            height="8"
            patternTransform="rotate(0)"
          >
            <path
              d="M0,4 C12.5,0 12.5,8 25,4 C37.5,0 37.5,8 50,4 C62.5,0 62.5,8 75,4 C87.5,0 87.5,8 100,4"
              fill="none"
              stroke="rgba(232, 180, 180, 1)"
              strokeWidth="0.6"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#wave-pattern)" />
      </svg>

      {/* Main Content */}
      <div className="max-w-lg w-full px-4 z-10">
        <div className="bg-white p-6 md:p-10 rounded-2xl shadow-lg w-full">
          <div className="text-center">
            <img
              className="mx-auto h-12 w-auto"
              src={Logo}
              alt="Manak Logo"
            />
            <h2 className="mt-6 text-3xl font-extrabold text-red-600">
              Forgot Password
            </h2>
            {step === 'email' ? (
              <p className="mt-2 text-sm text-gray-600">
                Enter your email address and we'll send you an OTP to reset your password.
              </p>
            ) : (
              <p className="mt-2 text-sm text-gray-600">
                Enter the 6-digit OTP sent to your email address.
              </p>
            )}
          </div>

          {step === 'email' ? (
            <form className="mt-8 space-y-6" onSubmit={handleSubmitEmail}>
              <div className="rounded-md shadow-sm -space-y-px">
                <InputField
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter your email"
                  icon={<MdEmail className="w-5 h-5" />}
                  required
                  labelClassName="text-red-600"
                />
              </div>

              {/* Role Selection */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-red-600 mb-2">
                  Account Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {roleOptions.map(role => (
                    <div
                      key={role.value}
                      onClick={() => setFormData({ ...formData, role: role.value })}
                      className={`
                        px-4 py-3 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center
                        ${
                          formData.role === role.value
                            ? 'border-red-600 bg-red-50 text-red-800 font-medium shadow-sm'
                            : 'border-gray-300 hover:border-gray-400'
                        }
                      `}
                    >
                      {role.display}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                    loading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? (
                    <>
                      <BiLoaderAlt className="animate-spin -ml-1 mr-3 h-5 w-5" />
                      Sending...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </div>

              <div className="text-center">
                <Link
                  to="/auth/login"
                  onClick={() => dispatch(resetAll())}
                  className="font-medium text-red-600 hover:text-red-500"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          ) : (
            <form className="mt-8 space-y-6" onSubmit={handleSubmitOTP}>
              <div>
                <label className="block text-sm font-medium text-red-600 mb-3">
                  <MdLockReset className="w-5 h-5 inline-block mr-1" />
                  Enter 6-digit OTP
                </label>
                <div className="flex justify-between gap-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <input
                      key={index}
                      ref={(el) => (otpInputs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={otpValues[index]}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      required
                    />
                  ))}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                    loading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? (
                    <>
                      <BiLoaderAlt className="animate-spin -ml-1 mr-3 h-5 w-5" />
                      Verifying...
                    </>
                  ) : (
                    'Verify OTP'
                  )}
                </button>
              </div>

              <div className="flex justify-between text-center">
                <button
                  type="button"
                  onClick={() => {
                    // Reset to email step
                    setStep('email');
                    
                    // Reset OTP values
                    setOtpValues(Array(6).fill(''));
                    
                    // Reset form data but keep email and role for UX
                    setFormData(prev => ({
                      ...prev,
                      otp: ''
                    }));
                    
                    // Reset Redux state completely to prevent auto-redirect
                    dispatch(resetAll());
                    dispatch(clearAuthError());
                    
                    // Force immediate state update to prevent loading state issues
                    setTimeout(() => {
                      dispatch({ 
                        type: 'auth/forgotPassword/fulfilled', 
                        payload: { success: false, userExists: false }
                      });
                    }, 10);
                  }}
                  className="font-medium text-gray-600 hover:text-gray-500"
                >
                  Back
                </button>
                <Link
                  to="/auth/login"
                  onClick={() => dispatch(resetAll())}
                  className="font-medium text-red-600 hover:text-red-500"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
